import React, { useCallback, useRef, useState } from 'react';
import { getAPIErrorReason } from '../../helpers/common';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import {
  ActionButton,
  CloseButton,
  Divider,
  ImageContainer,
  PreviewActions,
  PreviewHeader,
  PreviewImage,
  PreviewWrapper,
  StyledImage,
  ScreenshotToolbar,
  ToolbarButton,
  ToolbarGroup,
  ImageDiv,
  ImageMask,
  ImageMaskContent,
  Overlay,
  ProgressPill
} from './styles';
import { ICON_BUTTON_SIZE, ICON_BUTTON_TYPE, ICON_SIZE, IMAGE_TYPE, ImageType } from '../../global/constants';
import { dispatchTaskError, dispatchTaskSuccess } from '../../services/taskServices';
import { useHotkeys } from 'react-hotkeys-hook';
import { createPortal } from 'react-dom';
import copy from 'clipboard-copy';
import { dispatchProjectSuccess } from '../../services/projectServices';
import IconButton from '../button/iconButton';

interface CustomImagePreviewProps {
  src: string;
  alt?: string;
  width?: number | string;
  height?: number | string;
  className?: string;
  type?: ImageType;
  ShowImageOptions?: boolean;
  ShowImageBorder?: boolean;
  progress?: number;
  isUploading?: boolean;
}

export const CustomImagePreview: React.FC<CustomImagePreviewProps> = ({
  src,
  alt = '',
  width,
  height,
  className,
  type,
  ShowImageOptions,
  ShowImageBorder,
  progress,
  isUploading
}) => {
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const initialPinchDistance = useRef<number | null>(null);
  const lastPinchZoom = useRef(1);

  const classes = ['image-container'];

  if (type === IMAGE_TYPE.FIGMA) classes.push('figma-image');
  if (type === IMAGE_TYPE.PRINT_SCREEN) classes.push('print-screen-image');
  if (type === IMAGE_TYPE.IMAGE) classes.push('image-preview');
  if (className) classes.push(className);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) return; // Only allow dragging when clicking on the image itself
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setStartPos({ x: e.clientX - position.x, y: e.clientY - position.y });
    document.body.style.cursor = 'grabbing';
  };

  // Fix the linter error by using an arrow function
  const throttle = <T extends unknown[]>(func: (...args: T) => void, limit: number) => {
    let inThrottle: boolean;
    return (...args: T) => {
      if (!inThrottle) {
        func(...args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  };

  const throttledMouseMove = throttle((e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    e.stopPropagation();
    const newX = e.clientX - startPos.x;
    const newY = e.clientY - startPos.y;
    requestAnimationFrame(() => {
      setPosition({ x: newX, y: newY });
    });
  }, 16); // Approximately 60fps

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    document.body.style.cursor = 'default';
    if (zoom === 1) {
      setPosition({ x: 0, y: 0 }); // Reset position if at actual size
    }
  };

  const getTouchDistance = (touches: React.TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (e.touches.length === 2) {
      initialPinchDistance.current = getTouchDistance(e.touches);
      lastPinchZoom.current = zoom;
      setIsDragging(false);
    } else if (e.touches.length === 1) {
      setIsDragging(true);
      setStartPos({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (e.touches.length === 2 && initialPinchDistance.current !== null) {
      const currentDistance = getTouchDistance(e.touches);
      const ratio = currentDistance / initialPinchDistance.current;
      const newZoom = Math.min(Math.max(lastPinchZoom.current * ratio, 1), 3);
      setZoom(newZoom);
    } else if (e.touches.length === 1 && isDragging) {
      const newX = e.touches[0].clientX - startPos.x;
      const newY = e.touches[0].clientY - startPos.y;
      requestAnimationFrame(() => {
        setPosition({ x: newX, y: newY });
      });
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (e.touches.length < 2) {
      initialPinchDistance.current = null;
    }
    if (e.touches.length === 0) {
      setIsDragging(false);
      if (zoom === 1) {
        setPosition({ x: 0, y: 0 });
      }
    }
  };

  // Get file name from URL
  const getFilename = (url: string) => {
    const decodedUrl = decodeURIComponent(url);
    const parts = decodedUrl?.split('/');
    const fullFilename = parts?.pop()?.split('?')[0];
    const nameWithoutSuffix = fullFilename?.replace(/_(\d+)(\.\w+)$/, '$2');
    return nameWithoutSuffix;
  };

  // Handle image download
  const handleDownload = useCallback(
    async (e: React.MouseEvent) => {
      try {
        e.stopPropagation();
        const response = await fetch(src);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = getFilename(src) || 'downloaded_image.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        dispatchTaskError(getAPIErrorReason(error) || 'Something went wrong!');
      }
    },
    [src]
  );

  const handleCopyUrl = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      await copy(src);
      dispatchProjectSuccess('Image url copied successfully!');
    },
    [src]
  );

  // Handle copy to clipboard
  const handleCopy = useCallback(
    async (e: React.MouseEvent) => {
      try {
        e.stopPropagation();
        const response = await fetch(src);
        const blob = await response.blob();

        const imageBitmap = await createImageBitmap(blob);
        const canvas = document.createElement('canvas');
        canvas.width = imageBitmap.width;
        canvas.height = imageBitmap.height;
        const ctx = canvas.getContext('2d');

        if (ctx) {
          ctx.drawImage(imageBitmap, 0, 0);
          const pngBlob = await new Promise<Blob>((resolve) =>
            canvas.toBlob((blob) => blob && resolve(blob), 'image/png')
          );

          const clipboardItem = new ClipboardItem({ 'image/png': pngBlob });
          await navigator.clipboard.write([clipboardItem]);
          dispatchTaskSuccess('Image copied to clipboard!');
        }
      } catch (error) {
        dispatchTaskError(getAPIErrorReason(error));
      }
    },
    [src]
  );

  const resetPreviewState = () => {
    setZoom(1);
    setRotation(0);
    setFlipH(false);
    setFlipV(false);
    setPosition({ x: 0, y: 0 });
  };

  const togglePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPreviewVisible) {
      resetPreviewState();
    }
    setIsPreviewVisible(!isPreviewVisible);
  };

  const handleWheelZoom = useCallback(
    (e: React.WheelEvent) => {
      e.stopPropagation();
      if (e.deltaY < 0 && zoom < 3) {
        setZoom((prev) => Math.min(prev + 0.1, 3)); // Zoom in
      } else if (e.deltaY > 0 && zoom > 1) {
        setZoom((prev) => Math.max(prev - 0.1, 1)); // Zoom out
      }
    },
    [zoom]
  );

  const handleRotate = useCallback((angle: number) => {
    setRotation((prev) => (prev + angle) % 360);
  }, []);

  const handleFlipHorizontal = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setFlipV((prev) => !prev);
  }, []);

  const handleFlipVertical = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setFlipH((prev) => !prev);
  }, []);

  const handleZoomIn = useCallback(() => {
    if (zoom < 3) {
      setZoom((prev) => Math.min(prev + 0.1, 3));
    }
  }, [zoom]);

  const handleZoomOut = useCallback(() => {
    if (zoom > 1) {
      setZoom((prev) => Math.max(prev - 0.1, 1));
    }
  }, [zoom]);

  const isZoomOutDisabled = zoom <= 1;
  const isZoomInDisabled = zoom >= 3;

  useHotkeys('esc', async (event) => {
    event.preventDefault();
    setIsPreviewVisible(false);
  });

  const previewContent = (
    <PreviewWrapper
      role='button'
      onMouseDown={handleMouseDown}
      onMouseMove={throttledMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheelZoom}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
    >
      <PreviewHeader>
        <PreviewActions>
          <ActionButton onClick={handleDownload}>
            <SVGIcon
              name='download-icon'
              width={ICON_SIZE.XL}
              height={ICON_SIZE.XL}
              viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
              fill='var(--text-white)'
            />
          </ActionButton>
          <ActionButton onClick={handleCopy}>
            <SVGIcon
              name='copy-icon'
              width={ICON_SIZE.XL}
              height={ICON_SIZE.XL}
              viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
              fill='var(--text-white)'
            />
          </ActionButton>
        </PreviewActions>
        <Divider />
        <CloseButton onClick={() => setIsPreviewVisible(false)}>
          <SVGIcon
            name='close-preview-icon'
            width={ICON_SIZE.XL}
            height={ICON_SIZE.XL}
            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
            fill='var(--text-white)'
          />
        </CloseButton>
      </PreviewHeader>
      <ImageDiv
        role='button'
        onClick={(e) => {
          togglePreview(e);
        }}
        // handle releasing outside the image
      >
        <PreviewImage
          src={src}
          alt={alt}
          zoom={zoom}
          rotation={rotation}
          flipH={flipH}
          flipV={flipV}
          position={position}
          isDragging={isDragging}
          onMouseDown={handleMouseDown}
          onMouseMove={throttledMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      </ImageDiv>

      <ScreenshotToolbar role='button' onClick={(e) => e.stopPropagation()}>
        <ToolbarGroup>
          <ToolbarButton onClick={handleFlipHorizontal}>
            <SVGIcon
              name='flip-h-icon'
              width={ICON_SIZE.XL}
              height={ICON_SIZE.XL}
              viewBox={`0 0 ${ICON_SIZE.XL} ${ICON_SIZE.XL}`}
            />
          </ToolbarButton>
          <ToolbarButton onClick={handleFlipVertical}>
            <SVGIcon
              name='flip-v-icon'
              width={ICON_SIZE.XL}
              height={ICON_SIZE.XL}
              viewBox={`0 0 ${ICON_SIZE.XL} ${ICON_SIZE.XL}`}
            />
          </ToolbarButton>
          <ToolbarButton onClick={() => handleRotate(-90)}>
            <SVGIcon
              name='rotate-left-icon'
              width={ICON_SIZE.XL}
              height={ICON_SIZE.XL}
              viewBox={`0 0 ${ICON_SIZE.XL} ${ICON_SIZE.XL}`}
            />
          </ToolbarButton>
          <ToolbarButton onClick={() => handleRotate(90)}>
            <SVGIcon
              name='rotate-right-icon'
              width={ICON_SIZE.XL}
              height={ICON_SIZE.XL}
              viewBox={`0 0 ${ICON_SIZE.XL} ${ICON_SIZE.XL}`}
            />
          </ToolbarButton>
          <ToolbarButton onClick={handleZoomOut} disabled={isZoomOutDisabled}>
            <SVGIcon
              name='zoom-out-icon'
              width={ICON_SIZE.XL}
              height={ICON_SIZE.XL}
              viewBox={`0 0 ${ICON_SIZE.XL} ${ICON_SIZE.XL}`}
              fill={isZoomOutDisabled ? 'var(--text-placeholder)' : 'var(--text-white)'}
            />
          </ToolbarButton>
          <ToolbarButton onClick={handleZoomIn} disabled={isZoomInDisabled}>
            <SVGIcon
              name='zoom-in-icon'
              width={ICON_SIZE.XL}
              height={ICON_SIZE.XL}
              viewBox={`0 0 ${ICON_SIZE.XL} ${ICON_SIZE.XL}`}
              fill={isZoomInDisabled ? 'var(--text-placeholder)' : 'var(--text-white)'}
            />
          </ToolbarButton>
        </ToolbarGroup>
      </ScreenshotToolbar>
    </PreviewWrapper>
  );

  return (
    <ImageContainer
      role='button'
      className={classes.join(' ')}
      onClick={() => {
        setIsPreviewVisible(false);
        resetPreviewState(); // Reset state when closing by clicking outside
      }}>
      <StyledImage src={src} alt={alt} width={width} height={height} onClick={togglePreview} isDragging={isDragging} />
      {isUploading && (
        <Overlay>
          <ProgressPill>{progress}%</ProgressPill>
        </Overlay>
      )}
      {ShowImageOptions ? (
        <ImageMask role='button' onClick={togglePreview}>
          <ImageMaskContent
            role='button'
            onClick={(e) => e.stopPropagation()} // Prevents triggering preview on icon click
          >
            <IconButton
              iconName='copy-icon'
              size={ICON_BUTTON_SIZE.SMALL}
              variant={ICON_BUTTON_TYPE.TERTIARY}
              onClick={handleCopy}
            />
            <IconButton
              iconName='download-icon'
              size={ICON_BUTTON_SIZE.SMALL}
              variant={ICON_BUTTON_TYPE.TERTIARY}
              onClick={handleDownload}
            />
            <IconButton
              iconName='copy-link-icon'
              size={ICON_BUTTON_SIZE.SMALL}
              variant={ICON_BUTTON_TYPE.TERTIARY}
              onClick={handleCopyUrl}
            />
          </ImageMaskContent>
        </ImageMask>
      ) : (
        <ImageMask role='button' showImageBorder={ShowImageBorder} onClick={togglePreview} />
      )}
      {isPreviewVisible && createPortal(previewContent, document.body)}
    </ImageContainer>
  );
};
